import { aggiungiGiorni, elencoMesi, elencoSettimane, meseDi, meseSuccessivo, primoGiornoDelMese, settimanaDi, ultimoGiornoDelMese, etichettaMese, formattaData, } from './date.js';
import { contribuisceAllaCassa } from './tipi.js';
/**
 * Se una voce di piano pesa su un periodo.
 *
 * Nel periodo **in corso** pesano solo le scadenze. Una stima a mese aperto
 * non si somma a niente: quel mese ha già i suoi incassi, le sue fatture e le
 * sue scadenze, che dicono cosa succede davvero. Prevedere 18 mila e vederne
 * entrare 23 non fa 41 — la stima è stata superata dai fatti, non è un'aggiunta.
 * Una stima serve dove i fatti non ci sono ancora: nei mesi futuri.
 *
 * Nel passato non pesa niente: lì esiste solo l'effettivo.
 */
export function pesaNelPeriodo(v, collocazione) {
    if (collocazione === 'PASSATO')
        return false;
    if (collocazione === 'FUTURO')
        return true;
    return v.fonte !== 'BUDGET' && !v.stima;
}
/**
 * I periodi del prospetto, con i loro confini. Esportata perché il dettaglio di
 * una cella deve usare **gli stessi** confini del calcolo: ricavarli una
 * seconda volta a occhio produrrebbe un elenco che non somma al totale che sta
 * spiegando, ed è il modo più rapido per far perdere fiducia a entrambi i
 * numeri.
 */
export function costruisciSecchi(o) {
    const storico = o.storico ?? 0;
    if (o.granularita === 'MESE') {
        const meseCorrente = meseDi(o.oggi);
        const primo = meseSuccessivo(meseCorrente, -storico);
        return elencoMesi(primo, storico + 1 + o.orizzonte).map((m) => ({
            chiave: m,
            etichetta: etichettaMese(m),
            inizio: primoGiornoDelMese(m),
            fine: ultimoGiornoDelMese(m),
            collocazione: m < meseCorrente ? 'PASSATO' : m === meseCorrente ? 'CORRENTE' : 'FUTURO',
        }));
    }
    const settimanaCorrente = settimanaDi(o.oggi);
    const partenza = elencoSettimane(o.oggi, 1)[0].inizio;
    const daInizio = elencoSettimane(shiftGiorni(partenza, -7 * storico), storico + 1 + o.orizzonte);
    return daInizio.map((s) => ({
        chiave: s.chiave,
        etichetta: `${formattaData(s.inizio).slice(0, 5)}–${formattaData(s.fine).slice(0, 5)}`,
        inizio: s.inizio,
        fine: s.fine,
        collocazione: s.chiave < settimanaCorrente ? 'PASSATO' : s.chiave === settimanaCorrente ? 'CORRENTE' : 'FUTURO',
    }));
}
function shiftGiorni(data, giorni) {
    const d = new Date(`${data}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + giorni);
    return d.toISOString().slice(0, 10);
}
function vuotoPerFonte() {
    return {
        MOVIMENTI: { entrateCent: 0, usciteCent: 0 },
        DOCUMENTI: { entrateCent: 0, usciteCent: 0 },
        PIANO: { entrateCent: 0, usciteCent: 0 },
        F24: { entrateCent: 0, usciteCent: 0 },
        SCADUTI: { entrateCent: 0, usciteCent: 0 },
    };
}
/**
 * Quanto ci si aspetta ancora, per un ramo di voci, in un periodo.
 *
 * Tre grandezze concorrono, e la regola le tiene insieme senza sommarle:
 *
 * - `G` = già transitato in banca in quel periodo (movimenti)
 * - `D` = documenti con data di cassa in quel periodo, per il residuo
 * - `P` = piano: budget manuale e ricorrenti
 *
 * I documenti pesano sempre per intero. Il piano pesa solo per quanto **eccede**
 * ciò che è già successo più i documenti: `max(P − G − D, 0)`. Altrimenti la
 * stessa spesa conterebbe due volte — una come ricorrente e una come bonifico o
 * fattura.
 *
 * Per le stime il confronto si fa sul **ramo**, non sulla singola voce. Era il difetto: una
 * ricorrente «Incassi ISP» su *Ricavi › Ricavi da servizi* vedeva solo i 1.057 €
 * arrivati su quella sottovoce, e ignorava i 30 mila fra incassi e fatture
 * registrati su *Ricavi*. Aggiungeva così 18.942 € sopra un mese che la stima
 * l'aveva già superata. Chi scrive un budget di ricavi pensa ai ricavi, non
 * alla sottovoce su cui l'estratto conto ha deciso di appoggiarli.
 *
 * Quel che eccede si ripartisce fra le voci di piano del ramo in proporzione al
 * loro importo, così la cella di ogni voce resta spiegabile.
 *
 * Senza `radiceDi` ogni voce è un ramo a sé: è la regola di prima, e serve a chi
 * chiama il motore senza l'albero delle categorie.
 */
function combina(gia, documenti, piano, radiceDi) {
    const daDocumenti = new Map();
    const daPiano = new Map();
    for (const [k, d] of documenti)
        if (d !== 0)
            daDocumenti.set(k, d);
    const ramo = (k) => {
        const { categoriaId, verso } = scomponiChiave(k);
        return chiaveContributo(categoriaId === null ? null : (radiceDi?.[categoriaId] ?? categoriaId), verso);
    };
    const rami = new Map();
    const di = (k) => {
        const r = ramo(k);
        const v = rami.get(r) ?? { g: 0, d: 0, voci: [] };
        rami.set(r, v);
        return v;
    };
    // Solo i rami che hanno un piano: gli altri non hanno niente da compensare.
    for (const [k, p] of piano)
        if (p !== 0)
            di(k).voci.push([k, p]);
    for (const [k, g] of gia) {
        const v = rami.get(ramo(k));
        if (v)
            v.g += g;
    }
    for (const [k, d] of documenti) {
        const v = rami.get(ramo(k));
        if (v)
            v.d += d;
    }
    for (const { g, d, voci } of rami.values()) {
        const p = voci.reduce((t, [, x]) => t + x, 0);
        const eccede = Math.max(p - g - d, 0);
        if (eccede === 0 || p <= 0)
            continue;
        // Ripartizione al centesimo: le quote arrotondate per difetto, e i
        // centesimi che avanzano alle voci con il resto più grande. Così le celle
        // sommano esattamente al totale del ramo.
        const quote = voci.map(([k, x]) => {
            const esatta = (eccede * x) / p;
            return { k, cent: Math.floor(esatta), resto: esatta - Math.floor(esatta) };
        });
        let avanzo = eccede - quote.reduce((t, q) => t + q.cent, 0);
        for (const q of [...quote].sort((a, b) => b.resto - a.resto)) {
            if (avanzo <= 0)
                break;
            q.cent += 1;
            avanzo -= 1;
        }
        for (const q of quote)
            if (q.cent !== 0)
                daPiano.set(q.k, q.cent);
    }
    return { daDocumenti, daPiano };
}
function chiaveContributo(categoriaId, verso) {
    return `${verso}::${categoriaId ?? ''}`;
}
function scomponiChiave(chiave) {
    const i = chiave.indexOf('::');
    const verso = chiave.slice(0, i);
    const cat = chiave.slice(i + 2);
    return { verso, categoriaId: cat === '' ? null : cat };
}
export function calcolaPrevisione(dati, opzioni) {
    const sogliaMinimaCent = opzioni.sogliaMinimaCent ?? 0;
    const cassaAttualeCent = dati.conti
        .filter((c) => contribuisceAllaCassa(c.tipo))
        .reduce((t, c) => t + c.saldoCent, 0);
    const fuoriCassaCent = dati.conti
        .filter((c) => !contribuisceAllaCassa(c.tipo))
        .reduce((t, c) => t + c.saldoCent, 0);
    const secchi = costruisciSecchi(opzioni);
    if (secchi.length === 0) {
        return {
            cassaAttualeCent,
            fuoriCassaCent,
            sogliaMinimaCent,
            periodi: [],
            primaAllerta: null,
            cammino: [],
            minimoDiCassa: null,
            primoGiornoSottoSoglia: null,
            causeDelMinimo: null,
        };
    }
    const dentro = (s, data) => data >= s.inizio && data <= s.fine;
    /**
     * La catena parte dalla cassa di **oggi**, non dall'inizio dello storico: il
     * saldo dei conti è un dato di adesso, e ricostruire all'indietro i saldi
     * passati richiederebbe la storia completa dei movimenti di ogni conto, che
     * con l'import da CSV non è detto ci sia. I periodi passati mostrano quindi
     * il flusso reale, e la catena comincia dal periodo corrente.
     */
    const indiceCorrente = Math.max(0, secchi.findIndex((s) => s.collocazione !== 'PASSATO'));
    /**
     * Ogni cifra che deve ancora muoversi, con il giorno in cui ci si aspetta che
     * si muova. È la materia del cammino giornaliero — vedi `camminoDiCassa` —
     * ed è raccolta **qui**, mentre si costruiscono i periodi, perché deve sommare
     * esattamente a quello che i periodi dicono: un secondo calcolo farebbe due
     * verità, e il saldo del 30 non tornerebbe con l'ultimo giorno del cammino.
     * Solo a mesi: il piano è mensile, e a settimane non ha un giorno da dare.
     */
    const flussiDatati = [];
    const giornaliero = opzioni.granularita === 'MESE';
    const periodi = secchi.map((s) => {
        const perFonte = vuotoPerFonte();
        const contributi = [];
        // — Effettivo: i movimenti già transitati. Valgono sui periodi passati e su
        //   quello corrente, dove convivono col previsionale come confronto.
        const daMovimenti = new Map();
        // Gli esclusi non entrano nel prospetto ma spostano la cassa: servono a
        // ricostruire i saldi passati senza lasciare un buco. Vedi
        // `movimentiEsclusiCent`.
        let movimentiEsclusiCent = 0;
        if (s.collocazione !== 'FUTURO') {
            for (const m of dati.movimenti) {
                if (!dentro(s, m.data))
                    continue;
                if (m.escluso) {
                    movimentiEsclusiCent += m.importoCent;
                    continue;
                }
                const verso = m.importoCent >= 0 ? 'ENTRATA' : 'USCITA';
                const k = chiaveContributo(m.categoriaId, verso);
                daMovimenti.set(k, (daMovimenti.get(k) ?? 0) + Math.abs(m.importoCent));
            }
        }
        // — Previsione: documenti residui e piano, dal periodo corrente in poi.
        const documentiGrezzi = new Map();
        const pianoGrezzo = new Map();
        const stimeGrezze = new Map();
        // Le voci che compongono il piano di ogni chiave, per dare un giorno alla
        // quota che `combina` ne ricava.
        const vociScadenze = new Map();
        const vociStime = new Map();
        // Le voci di una controparte precisa, e quanto quella controparte ha già
        // fatturato nel periodo: vedi `VocePiano.anagraficaId`.
        const vociDiControparte = [];
        const fatturatoDi = new Map();
        /** Il giorno a partire dal quale il periodo può ancora muovere denaro. */
        const primoGiornoUtile = s.inizio > opzioni.oggi ? s.inizio : opzioni.oggi;
        const nonPrimaDiOggi = (data) => (data < primoGiornoUtile ? primoGiornoUtile : data);
        /**
         * L'arretrato che il periodo in corso si trascina.
         *
         * Un documento pesa dal periodo della sua data di cassa in poi: se quella
         * data è passata non ricade in nessun periodo generato, e finora spariva.
         * Ma una fattura non incassata resta un credito che ci si aspetta di
         * incassare, e una non pagata resta un debito da onorare: farli sparire dal
         * prospetto significa non prevederli affatto, che è il difetto peggiore per
         * uno strumento che deve dire quanto serve il mese prossimo.
         *
         * Si posano **sul periodo in corso**, non sul successivo: è la stessa regola
         * degli F24 scaduti, e un programma che colloca le cose in ritardo in due
         * modi diversi costringe a ricordarsi quale sia quale.
         *
         * Restano una fonte a sé e non entrano in `DOCUMENTI` perché sono un'altra
         * cosa: una scadenza del periodo è attesa, un arretrato di mesi è un
         * rischio. Confusi insieme, chi guarda non può più distinguerli — e parte
         * di questi crediti potrebbe essere già incassata e soltanto non
         * riconciliata, il che rende la distinzione ancora più necessaria.
         */
        const scadutiGrezzi = new Map();
        if (s.collocazione !== 'PASSATO') {
            for (const d of dati.documenti) {
                if (d.residuoCent === 0)
                    continue;
                /**
                  * Arretrato è ciò che era atteso **prima che questo periodo
                  * cominciasse**, non ciò che è atteso prima di oggi.
                  *
                  * La differenza si vede il 17 del mese su una fattura scaduta il 15:
                  * con il confronto su «oggi» finiva fra gli arretrati — dentro una
                  * sezione che si chiama «dai periodi precedenti» — mentre è una
                  * scadenza di questo mese che può benissimo essere pagata il 20. Il
                  * mese non è ancora finito, e trattare come arretrato tutto ciò che
                  * non è già stato incassato lo dà per perso in anticipo.
                  *
                  * Il confronto con `s.inizio` vale anche a settimane senza dover
                  * scrivere una seconda regola: lì «prima dell'inizio del periodo» è
                  * prima del lunedì, ed è la stessa frase.
                  */
                if (d.dataCassaAttesa < s.inizio) {
                    if (s.collocazione !== 'CORRENTE')
                        continue;
                    const k = chiaveContributo(d.categoriaId, d.verso);
                    scadutiGrezzi.set(k, (scadutiGrezzi.get(k) ?? 0) + d.residuoCent);
                    continue;
                }
                if (!dentro(s, d.dataCassaAttesa))
                    continue;
                const k = chiaveContributo(d.categoriaId, d.verso);
                documentiGrezzi.set(k, (documentiGrezzi.get(k) ?? 0) + d.residuoCent);
                if (d.anagraficaId) {
                    const ka = `${d.anagraficaId}|${d.verso}`;
                    fatturatoDi.set(ka, (fatturatoDi.get(ka) ?? 0) + d.residuoCent);
                }
                // Una fattura di questo mese con la data già passata non è arretrato,
                // ma nemmeno può muoversi ieri: si colloca oggi, come l'F24 scaduto.
                if (giornaliero) {
                    flussiDatati.push({
                        data: nonPrimaDiOggi(d.dataCassaAttesa),
                        importoCent: d.verso === 'ENTRATA' ? d.residuoCent : -d.residuoCent,
                        etichetta: d.etichetta,
                    });
                }
            }
            for (const v of dati.piano) {
                const appartiene = opzioni.granularita === 'MESE' ? v.mese === s.chiave : dentro(s, primoGiornoDelMese(v.mese));
                if (!appartiene)
                    continue;
                if (!pesaNelPeriodo(v, s.collocazione))
                    continue;
                if (v.anagraficaId) {
                    vociDiControparte.push(v);
                    continue;
                }
                const k = chiaveContributo(v.categoriaId, v.verso);
                const stima = v.fonte === 'BUDGET' || v.stima;
                const dove = stima ? stimeGrezze : pianoGrezzo;
                dove.set(k, (dove.get(k) ?? 0) + v.importoCent);
                const voci = stima ? vociStime : vociScadenze;
                voci.set(k, [...(voci.get(k) ?? []), v]);
            }
        }
        // — F24: ha una data propria e non deriva da documenti o piano, quindi non
        //   passa dalla compensazione col già avvenuto. Un versamento già uscito
        //   dal conto si vede fra i movimenti, e sommarlo due volte sarebbe il
        //   difetto peggiore proprio sul numero che si guarda per non restare corti.
        const f24Grezza = new Map();
        // Anche il periodo **in corso**: un modulo scaduto e non ancora versato
        // viene collocato a oggi, e ignorarlo qui vorrebbe dire non prevederlo
        // proprio nel periodo in cui bisogna trovargli i soldi.
        if (s.collocazione !== 'PASSATO') {
            for (const v of dati.f24 ?? []) {
                if (v.importoCent <= 0 || !dentro(s, v.data))
                    continue;
                const k = chiaveContributo(v.categoriaId, 'USCITA');
                f24Grezza.set(k, (f24Grezza.get(k) ?? 0) + v.importoCent);
                if (giornaliero)
                    flussiDatati.push({ data: nonPrimaDiOggi(v.data), importoCent: -v.importoCent, etichetta: v.etichetta ?? 'F24' });
            }
        }
        // Le scadenze fisse si confrontano con la loro voce: la rata del leasing
        // non la copre il carburante, anche se stanno tutti e due su Autoveicoli.
        // Le stime con il ramo intero: chi prevede i ricavi pensa ai ricavi, non
        // alla sottovoce su cui l'estratto conto li ha appoggiati.
        const { daDocumenti, daPiano: daScadenze } = combina(daMovimenti, documentiGrezzi, pianoGrezzo);
        const { daPiano: daStime } = combina(daMovimenti, documentiGrezzi, stimeGrezze, dati.radiceDi);
        const daPiano = new Map(daScadenze);
        for (const [k, v] of daStime)
            daPiano.set(k, (daPiano.get(k) ?? 0) + v);
        /**
         * Le stime di una controparte: pesano per quanto la controparte non ha già
         * fatturato nel periodo. La fattura vera prende il posto della stima, e non
         * si somma: se Xstream a novembre ha già mandato 1.500 €, la sua stima da
         * 1.473 € quel mese non aggiunge niente.
         */
        for (const v of vociDiControparte) {
            const ka = `${v.anagraficaId}|${v.verso}`;
            const gia = fatturatoDi.get(ka) ?? 0;
            const quota = Math.max(v.importoCent - Math.max(gia, 0), 0);
            // Quello che la fattura ha coperto non copre anche una seconda voce della
            // stessa controparte.
            fatturatoDi.set(ka, Math.max(gia - v.importoCent, 0));
            if (quota === 0)
                continue;
            const k = chiaveContributo(v.categoriaId, v.verso);
            daPiano.set(k, (daPiano.get(k) ?? 0) + quota);
            if (giornaliero && s.collocazione !== 'PASSATO') {
                flussiDatati.push({
                    data: giornoDellaVoce(v, s, v.verso, primoGiornoUtile),
                    importoCent: v.verso === 'ENTRATA' ? quota : -quota,
                    etichetta: v.etichetta ?? 'Budget',
                });
            }
        }
        if (giornaliero && s.collocazione !== 'PASSATO') {
            for (const [quote, voci] of [
                [daScadenze, vociScadenze],
                [daStime, vociStime],
            ]) {
                for (const [k, quota] of quote) {
                    const { verso } = scomponiChiave(k);
                    for (const { voce, importoCent } of ripartisci(quota, voci.get(k) ?? [])) {
                        flussiDatati.push({
                            data: giornoDellaVoce(voce, s, verso, primoGiornoUtile),
                            importoCent: verso === 'ENTRATA' ? importoCent : -importoCent,
                            etichetta: voce?.etichetta ?? 'Budget',
                        });
                    }
                }
            }
        }
        const registra = (mappa, fonte) => {
            for (const [k, importoCent] of mappa) {
                if (importoCent === 0)
                    continue;
                const { categoriaId, verso } = scomponiChiave(k);
                contributi.push({ categoriaId, verso, fonte, importoCent });
                if (verso === 'ENTRATA')
                    perFonte[fonte].entrateCent += importoCent;
                else
                    perFonte[fonte].usciteCent += importoCent;
            }
        };
        registra(daMovimenti, 'MOVIMENTI');
        registra(daDocumenti, 'DOCUMENTI');
        registra(daPiano, 'PIANO');
        registra(f24Grezza, 'F24');
        // Non passa da `combina`: un arretrato non è una previsione che il già
        // avvenuto possa compensare. Quel che l'ha davvero chiuso è un abbinamento,
        // che ne azzera il residuo e lo fa uscire da qui da solo.
        registra(scadutiGrezzi, 'SCADUTI');
        // Residuo = ciò che deve ancora succedere nel periodo, senza l'arretrato;
        // totale = residuo più il già avvenuto più l'arretrato.
        const entrateResidueCent = perFonte.DOCUMENTI.entrateCent + perFonte.PIANO.entrateCent;
        const usciteResidueCent = perFonte.DOCUMENTI.usciteCent + perFonte.PIANO.usciteCent + perFonte.F24.usciteCent;
        const entrateCent = perFonte.MOVIMENTI.entrateCent + entrateResidueCent + perFonte.SCADUTI.entrateCent;
        const usciteCent = perFonte.MOVIMENTI.usciteCent + usciteResidueCent + perFonte.SCADUTI.usciteCent;
        return {
            chiave: s.chiave,
            etichetta: s.etichetta,
            inizio: s.inizio,
            fine: s.fine,
            collocazione: s.collocazione,
            saldoInizialeCent: null,
            entrateCent,
            usciteCent,
            flussoNettoCent: entrateCent - usciteCent,
            entrateResidueCent,
            usciteResidueCent,
            flussoResiduoCent: entrateResidueCent - usciteResidueCent,
            movimentiEsclusiCent,
            saldoFinaleCent: null,
            inAllerta: false,
            perFonte,
            contributi,
        };
    });
    /**
     * Quanto la cassa si è mossa **davvero** in un periodo già cominciato:
     * il flusso del prospetto più i giroconti che il prospetto non guarda.
     */
    const variazioneDiCassa = (p) => p.perFonte.MOVIMENTI.entrateCent - p.perFonte.MOVIMENTI.usciteCent + p.movimentiEsclusiCent;
    // La catena, in avanti: ogni periodo apre dove ha chiuso il precedente. Se la
    // chiusura reale si sposta, si spostano con lei tutti i saldi successivi —
    // altrimenti «saldo finale = saldo iniziale del mese seguente» è una bugia
    // visibile. Il periodo in corso apre con la cassa di oggi **meno** quello che
    // ci è già passato: è il primo giorno del mese, non adesso.
    let saldo = cassaAttualeCent;
    for (let i = indiceCorrente; i < periodi.length; i++) {
        periodi[i].saldoInizialeCent = i === indiceCorrente ? saldo - variazioneDiCassa(periodi[i]) : saldo;
        saldo += periodi[i].flussoResiduoCent;
        periodi[i].saldoFinaleCent = saldo;
        periodi[i].inAllerta = saldo < sogliaMinimaCent;
    }
    /**
     * La stessa catena all'indietro, sui periodi chiusi — e lì non è una
     * previsione: è la cassa che c'era davvero.
     *
     * Si risale dalla cassa di oggi sottraendo quello che si è mosso. Vale al
     * centesimo perché per ogni conto `saldo di oggi = saldo iniziale + somma dei
     * movimenti`, ma solo da quando la storia è completa su tutti i conti: prima
     * di `inizioStoriaCassa` i saldi restano `null`, perché un conto che ancora
     * non c'era darebbe un numero pulito e falso.
     */
    let indietro = periodi[indiceCorrente]?.saldoInizialeCent ?? cassaAttualeCent;
    for (let i = indiceCorrente - 1; i >= 0; i--) {
        const noto = !dati.inizioStoriaCassa || periodi[i].inizio >= dati.inizioStoriaCassa;
        if (!noto)
            break;
        periodi[i].saldoFinaleCent = indietro;
        indietro -= variazioneDiCassa(periodi[i]);
        periodi[i].saldoInizialeCent = indietro;
    }
    // `inAllerta` resta una faccenda del presente e del futuro: un mese chiuso
    // sotto soglia è storia, e farlo comparire fra gli allarmi manderebbe a
    // rimediare a una cosa già successa.
    const primaAllerta = periodi.slice(indiceCorrente).find((p) => p.inAllerta) ?? null;
    const { cammino, minimo } = camminoDiCassa(cassaAttualeCent, opzioni.oggi, flussiDatati);
    const sotto = cammino.find((g) => g.minimoCent < sogliaMinimaCent);
    return {
        cassaAttualeCent,
        fuoriCassaCent,
        sogliaMinimaCent,
        periodi,
        primaAllerta,
        cammino: giornaliero ? cammino : [],
        minimoDiCassa: giornaliero ? minimo : null,
        primoGiornoSottoSoglia: giornaliero && sotto ? { data: sotto.data, saldoCent: sotto.minimoCent } : null,
        causeDelMinimo: giornaliero ? causeDelMinimo(flussiDatati, minimo.data) : null,
    };
}
/**
 * Le uscite più grosse fino al fondo — compreso il giorno stesso, perché è lì
 * che le uscite lo scavano — e gli incassi più grossi nel mese dopo.
 */
export function causeDelMinimo(flussiGrezzi, dataMinimo, quante = 5) {
    // Una fattura entra nel calcolo divisa in imponibile e quota IVA, e una rata
    // di budget in tante voci quante le ricorrenti: qui torna una cosa sola, o la
    // stessa fattura comparirebbe due volte fra le cause.
    const uniti = new Map();
    for (const f of flussiGrezzi) {
        const chiave = `${f.data}|${f.etichetta ?? ''}|${f.importoCent < 0 ? '-' : '+'}`;
        const c = uniti.get(chiave);
        if (c)
            c.importoCent += f.importoCent;
        else
            uniti.set(chiave, { ...f });
    }
    const flussi = [...uniti.values()];
    const finoA = flussi.filter((f) => f.importoCent < 0 && f.data <= dataMinimo);
    const limite = aggiungiGiorni(dataMinimo, 30);
    const dopo = flussi.filter((f) => f.importoCent > 0 && f.data > dataMinimo && f.data <= limite);
    return {
        uscite: [...finoA].sort((a, b) => a.importoCent - b.importoCent).slice(0, quante),
        entrateSubitoDopo: [...dopo].sort((a, b) => b.importoCent - a.importoCent).slice(0, 3),
    };
}
/**
 * La cassa giorno per giorno, a partire da quella di oggi.
 *
 * Dentro la stessa giornata **le uscite vengono prima delle entrate**: se lo
 * stipendio e un incasso cadono lo stesso giorno, non si sa quale arrivi prima,
 * e per la domanda «posso spendere?» l'ipotesi giusta è quella che non ti
 * sorprende in negativo. Il minimo tiene conto di quel momento, non solo del
 * saldo a fine giornata.
 */
export function camminoDiCassa(cassaDiOggiCent, oggi, flussi) {
    const perGiorno = new Map();
    for (const f of flussi) {
        if (f.importoCent === 0)
            continue;
        const g = perGiorno.get(f.data) ?? { entrate: 0, uscite: 0 };
        if (f.importoCent > 0)
            g.entrate += f.importoCent;
        else
            g.uscite += -f.importoCent;
        perGiorno.set(f.data, g);
    }
    let saldo = cassaDiOggiCent;
    let minimo = { data: oggi, saldoCent: saldo };
    const cammino = [{ data: oggi, saldoCent: saldo, minimoCent: saldo }];
    for (const data of [...perGiorno.keys()].sort()) {
        const g = perGiorno.get(data);
        const dopoLeUscite = saldo - g.uscite;
        if (dopoLeUscite < minimo.saldoCent)
            minimo = { data, saldoCent: dopoLeUscite };
        saldo = dopoLeUscite + g.entrate;
        const giorno = { data, saldoCent: saldo, minimoCent: dopoLeUscite };
        if (data === oggi)
            cammino[0] = giorno;
        else
            cammino.push(giorno);
    }
    return { cammino, minimo };
}
/**
 * Dove finisce il punto più basso della cassa se oggi si decide una spesa.
 *
 * È la domanda «posso permettermelo?» detta in numeri: la spesa esce il giorno
 * scelto e da lì abbassa ogni giorno successivo, quindi il nuovo minimo è il
 * più basso fra quello che succede prima (invariato) e quello che succede dopo
 * (meno la spesa). Il giorno della spesa conta anche se quel giorno non si
 * muove nient'altro: è lì che i soldi escono.
 *
 * Lavora sul cammino e non ricalcola la previsione: è istantanea, e resta la
 * stessa aritmetica del margine che le sta accanto.
 */
export function simulaSpesa(cammino, dataSpesa, importoCent) {
    if (cammino.length === 0)
        return null;
    let minimo = null;
    const candida = (data, saldoCent) => {
        if (minimo === null || saldoCent < minimo.saldoCent)
            minimo = { data, saldoCent };
    };
    // Il saldo con cui si apre il giorno della spesa, se quel giorno non ha un
    // punto suo: è l'ultimo saldo prima, o la cassa di oggi.
    const prima = cammino.filter((g) => g.data < dataSpesa).at(-1);
    if (!cammino.some((g) => g.data === dataSpesa)) {
        candida(dataSpesa, (prima ?? cammino[0]).saldoCent - importoCent);
    }
    for (const g of cammino)
        candida(g.data, g.data < dataSpesa ? g.minimoCent : g.minimoCent - importoCent);
    return minimo;
}
/**
 * Divide la quota di piano di una voce fra le ricorrenti che la compongono, in
 * proporzione al loro importo e al centesimo: le quote sommano esattamente alla
 * quota, così il cammino chiude il mese dove lo chiude il periodo.
 */
function ripartisci(quotaCent, voci) {
    const totale = voci.reduce((t, v) => t + v.importoCent, 0);
    if (voci.length === 0 || totale <= 0)
        return [{ voce: undefined, importoCent: quotaCent }];
    const parti = voci.map((v) => {
        const esatta = (quotaCent * v.importoCent) / totale;
        return { voce: v, importoCent: Math.floor(esatta), resto: esatta - Math.floor(esatta) };
    });
    let avanzo = quotaCent - parti.reduce((t, p) => t + p.importoCent, 0);
    for (const p of [...parti].sort((a, b) => b.resto - a.resto)) {
        if (avanzo <= 0)
            break;
        p.importoCent += 1;
        avanzo -= 1;
    }
    return parti.map(({ voce, importoCent }) => ({ voce, importoCent }));
}
/**
 * Il giorno in cui una voce di piano muove denaro.
 *
 * Una ricorrente ha il suo giorno — lo stipendio il 27, la rata il 1° — e lo
 * usa, spostato a oggi se nel mese in corso è già passato ma la voce deve
 * ancora muoversi. Un budget manuale un giorno non ce l'ha, e qui serve
 * scegliere: **le uscite al primo giorno utile, le entrate all'ultimo**. È
 * l'ipotesi prudente — i soldi escono prima e arrivano dopo — ed è quella giusta
 * per una cifra che serve a decidere se si può spendere. Distribuirle sui giorni
 * sarebbe inventare un ritmo che nessuno ha dichiarato.
 */
function giornoDellaVoce(voce, s, verso, primoGiornoUtile) {
    if (voce?.giorno) {
        const ultimo = Number(s.fine.slice(8, 10));
        const giorno = String(Math.min(voce.giorno, ultimo)).padStart(2, '0');
        const data = `${s.chiave}-${giorno}`;
        return data < primoGiornoUtile ? primoGiornoUtile : data;
    }
    return verso === 'ENTRATA' ? s.fine : primoGiornoUtile;
}
/**
 * Espande le ricorrenti nelle voci di piano dei mesi richiesti.
 *
 * L'importo non è un valore ma una **storia di valori con validità**: cambiare
 * lo stipendio da agosto non deve riscrivere i mesi già chiusi, altrimenti il
 * confronto Effettivo/Previsione sul passato perde significato. Per ogni mese
 * si prende quindi la versione in vigore al primo giorno del mese.
 */
export function espandiRicorrenti(ricorrenti, mesi) {
    const voci = [];
    for (const r of ricorrenti) {
        if (r.passoMesi <= 0)
            continue;
        for (const mese of mesi) {
            if (mese < r.primoMese)
                continue;
            if (r.ultimoMese && mese > r.ultimoMese)
                continue;
            const distanza = distanzaMesi(r.primoMese, mese);
            if (distanza % r.passoMesi !== 0)
                continue;
            const versione = versioneInVigore(r.versioni, primoGiornoDelMese(mese));
            if (!versione)
                continue;
            voci.push({
                mese,
                categoriaId: r.categoriaId,
                verso: r.verso,
                importoCent: importoAllaRicorrenza(r, versione, mese),
                fonte: 'RICORRENTE',
                stima: r.natura === 'STIMA',
                giorno: r.giornoDelMese,
                anagraficaId: r.anagraficaId ?? undefined,
                etichetta: r.nome,
            });
        }
    }
    return voci;
}
/**
 * L'importo di una ricorrenza, cresciuto di tante volte quante sono le
 * ricorrenze passate **da quando vale questa versione**.
 *
 * Si arrotonda ogni volta partendo dalla base, non dal valore precedente: con
 * l'arrotondamento a catena l'errore si accumula, e dopo dodici mesi la cifra
 * non è più quella che nessuna moltiplicazione produce.
 */
export function importoAllaRicorrenza(r, versione, mese) {
    return importoCresciuto(versione.importoCent, versione.crescitaPercentuale ?? 0, ricorrenzeDaInizioVersione(r, versione, mese));
}
/**
 * La formula, in un posto solo: `base · (1 + p)ⁿ`, arrotondata **dalla base**.
 *
 * A catena l'errore del centesimo si somma, e dopo un anno la cifra non è più
 * quella che nessuna moltiplicazione produce. Sta qui e non in una schermata
 * perché l'anteprima che si vede mentre si sceglie la percentuale dev'essere
 * lo stesso calcolo che finisce nel prospetto: due copie che divergono di un
 * centesimo fanno perdere fiducia a tutti e due i numeri.
 */
export function importoCresciuto(baseCent, crescitaPercentuale, ricorrenze) {
    if (!crescitaPercentuale || ricorrenze <= 0)
        return baseCent;
    return Math.round(baseCent * (1 + crescitaPercentuale / 100) ** ricorrenze);
}
/** Le prime `quante` ricorrenze, per far vedere cosa produce una percentuale. */
export function serieCrescita(baseCent, crescitaPercentuale, quante) {
    return Array.from({ length: Math.max(0, quante) }, (_, i) => importoCresciuto(baseCent, crescitaPercentuale, i));
}
/**
 * Quante ricorrenze separano `mese` dalla prima che ricade in questa versione.
 *
 * Una versione entra in vigore il primo giorno del mese successivo se la sua
 * decorrenza cade a metà mese: è la stessa regola che decide quale importo
 * vale, e contare da un mese in cui la versione non valeva ancora farebbe
 * partire la crescita da un gradino più in alto.
 */
function ricorrenzeDaInizioVersione(r, versione, mese) {
    const meseDecorrenza = versione.validaDa.slice(0, 7);
    const primoMeseValido = primoGiornoDelMese(meseDecorrenza) >= versione.validaDa ? meseDecorrenza : mesePiu(meseDecorrenza, 1);
    // La prima ricorrenza dentro la versione: le occorrenze cadono ogni
    // `passoMesi` a partire da `primoMese`, e la versione può cominciare in mezzo.
    const scarto = distanzaMesi(r.primoMese, primoMeseValido);
    const passi = scarto <= 0 ? 0 : Math.ceil(scarto / r.passoMesi);
    const primaRicorrenza = mesePiu(r.primoMese, passi * r.passoMesi);
    return Math.floor(distanzaMesi(primaRicorrenza, mese) / r.passoMesi);
}
function mesePiu(mese, quanti) {
    const [anno, m] = mese.split('-').map(Number);
    const totale = (anno * 12 + (m - 1)) + quanti;
    return `${String(Math.floor(totale / 12)).padStart(4, '0')}-${String((totale % 12) + 1).padStart(2, '0')}`;
}
/** La versione in vigore a una data: `[validaDa, validaA)`. */
export function versioneInVigore(versioni, data) {
    return versioni.find((x) => x.validaDa <= data && (x.validaA === null || data < x.validaA)) ?? null;
}
function distanzaMesi(da, a) {
    const [a1, m1] = da.split('-').map(Number);
    const [a2, m2] = a.split('-').map(Number);
    return (a2 - a1) * 12 + (m2 - m1);
}
/**
 * L'importo di base in vigore a una data — **senza** la crescita, che dipende
 * da quale ricorrenza si sta guardando. Per l'importo di un mese preciso serve
 * `importoAllaRicorrenza`.
 */
export function importoInVigore(versioni, data) {
    return versioneInVigore(versioni, data)?.importoCent ?? null;
}
/** Oltre questa variabilità una controparte non è un canone, ma a volte ci somiglia. */
export const VARIABILITA_STABILE = 25;
/** Oltre questa, gli importi sono troppo diversi per stimarne uno solo. */
export const VARIABILITA_MASSIMA = 60;
/**
 * Le controparti che si ripetono abbastanza da meritare una voce ricorrente,
 * con il ritmo con cui tornano e quando torneranno.
 *
 * Il previsionale, nei mesi futuri, conosce solo le fatture già arrivate: i
 * costi che tornano — la connettività ogni mese, il commercialista ogni
 * trimestre — mancano finché la fattura non c'è, e i mesi lontani sembrano più
 * ricchi di quanto saranno. Qui lo storico diventa previsione.
 *
 * Le scelte, tutte per non proporre il falso:
 *
 * - **la frequenza** si legge dalle distanze fra i mesi in cui ha fatturato:
 *   quasi tutti i mesi è mensile; distanze tutte di due o di tre mesi sono
 *   bimestrale o trimestrale. Il resto è irregolare, e si propone solo se è
 *   successo almeno tre volte;
 * - **l'importo** è la mediana, non la media (che si mostra accanto): Amazon va
 *   da 120 a 9.353 € al mese, e la media direbbe una cifra mai successa;
 * - **quando torna**: l'ultima volta più il passo, e mai prima del mese
 *   prossimo — il mese in corso ha già i suoi fatti;
 * - **il giorno** viene dai pagamenti veri quando ce ne sono almeno tre, perché
 *   è quando i soldi si muovono che conta per la cassa; altrimenti dalle
 *   scadenze.
 *
 * Propone e basta: niente entra nel budget finché qualcuno non lo sceglie.
 */
export function proponiRicorrenti(osservazioni, mesi, meseCorrente) {
    const mediana = (valori) => {
        const o = [...valori].sort((a, b) => a - b);
        const m = Math.floor(o.length / 2);
        return o.length % 2 ? o[m] : Math.round((o[m - 1] + o[m]) / 2);
    };
    const finestra = mesi.length;
    const proposte = [];
    for (const o of osservazioni) {
        const presenti = o.totaliMensiliCent.map((t, i) => (t > 0 ? i : -1)).filter((i) => i >= 0);
        if (presenti.length < 2)
            continue;
        const importi = presenti.map((i) => o.totaliMensiliCent[i]);
        const distanze = presenti.slice(1).map((m, i) => m - presenti[i]);
        let frequenza;
        let passo;
        if (presenti.length >= finestra - 1) {
            frequenza = 'MENSILE';
            passo = 1;
        }
        else if (distanze.every((d) => d === 2) && presenti.length >= 2) {
            frequenza = 'BIMESTRALE';
            passo = 2;
        }
        else if (distanze.every((d) => d === 3)) {
            frequenza = 'TRIMESTRALE';
            passo = 3;
        }
        else {
            // Irregolare: si propone solo se è successo abbastanza volte da essere
            // una tendenza e non due coincidenze.
            if (presenti.length < 3)
                continue;
            frequenza = 'IRREGOLARE';
            // Il passo più vicino fra quelli che una voce ricorrente sa esprimere:
            // un «ogni 4 mesi» non esiste nel budget, e proporlo lo renderebbe
            // impossibile da aggiungere così com'è.
            const tipica = mediana(distanze);
            passo = [1, 2, 3, 6].reduce((m, p) => (Math.abs(p - tipica) < Math.abs(m - tipica) ? p : m), 1);
        }
        const media = importi.reduce((t, v) => t + v, 0) / importi.length;
        const scarto = Math.sqrt(importi.reduce((t, v) => t + (v - media) ** 2, 0) / importi.length);
        const variabilita = media > 0 ? Math.round((scarto / media) * 100) : 0;
        // Mensile con almeno cinque presenze è un fatto; bimestrale e trimestrale
        // in sei mesi si vedono tre e due volte, e restano probabili.
        const confidenza = frequenza === 'IRREGOLARE' || variabilita > VARIABILITA_MASSIMA
            ? 'DA_VERIFICARE'
            : frequenza === 'MENSILE' && variabilita <= VARIABILITA_STABILE
                ? 'CERTO'
                : 'PROBABILE';
        let prossimo = meseSuccessivo(mesi[presenti[presenti.length - 1]], passo);
        const primoUtile = meseSuccessivo(meseCorrente, 1);
        while (prossimo < primoUtile)
            prossimo = meseSuccessivo(prossimo, passo);
        const daiPagamenti = o.giorniPagamento.length >= 3;
        const giorni = daiPagamenti ? o.giorniPagamento : o.giorniScadenza;
        proposte.push({
            anagraficaId: o.anagraficaId,
            nome: o.nome,
            verso: o.verso,
            frequenza,
            passoMesi: passo,
            confidenza,
            occorrenze: presenti.length,
            mesiFinestra: finestra,
            importoCent: mediana(importi),
            mediaCent: Math.round(media),
            minimoCent: Math.min(...importi),
            massimoCent: Math.max(...importi),
            ultimiImportiCent: importi.slice(-6),
            variabilita,
            prossimoMese: prossimo,
            giorno: giorni.length > 0 ? Math.min(31, Math.max(1, mediana(giorni))) : 1,
            giornoDa: daiPagamenti ? 'PAGAMENTI' : 'SCADENZE',
        });
    }
    const ordine = { CERTO: 0, PROBABILE: 1, DA_VERIFICARE: 2 };
    return proposte.sort((a, b) => ordine[a.confidenza] - ordine[b.confidenza] || b.importoCent - a.importoCent);
}
//# sourceMappingURL=previsionale.js.map